import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-4xl font-black text-white">Privacy Policy</h1>
          <p className="text-slate-400 mt-2">Last updated: March 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-4">
              Gantt Account Management (Pty) Ltd ("we", "our", or "us") is committed to protecting your privacy 
              and ensuring the security of your personal information. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our construction management software 
              and related services.
            </p>
            <p className="text-slate-600">
              This policy complies with the Protection of Personal Information Act 4 of 2013 (POPIA) and other 
              applicable South African data protection laws.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3">2.1 Personal Information</h3>
            <p className="text-slate-600 mb-4">We may collect the following personal information:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Name, surname, and job title</li>
              <li>Email address and telephone numbers</li>
              <li>Company name and business address</li>
              <li>Professional qualifications and registrations</li>
              <li>Login credentials and authentication data</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">2.2 Project and Business Data</h3>
            <p className="text-slate-600 mb-4">In the course of providing our services, we process:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Construction project details and documentation</li>
              <li>Delay event records and claim information</li>
              <li>Site instructions, RFIs, and variation orders</li>
              <li>Meeting minutes and correspondence</li>
              <li>Programme schedules and IRS data</li>
              <li>Financial information related to claims and variations</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">2.3 Technical Data</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>IP addresses and browser information</li>
              <li>Device identifiers and operating system</li>
              <li>Usage data and interaction logs</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use collected information for the following purposes:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Providing and maintaining our construction management services</li>
              <li>Generating AI-powered delay notices and EOT claims</li>
              <li>Processing and storing project documentation</li>
              <li>Communicating with you about your account and projects</li>
              <li>Sending email notifications and document sharing</li>
              <li>Improving our services and developing new features</li>
              <li>Ensuring security and preventing fraud</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Legal Basis for Processing</h2>
            <p className="text-slate-600 mb-4">Under POPIA, we process your personal information based on:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Contractual necessity:</strong> Processing required to fulfil our service agreement with you</li>
              <li><strong>Legitimate interests:</strong> Processing necessary for our legitimate business interests</li>
              <li><strong>Consent:</strong> Where you have given explicit consent for specific processing activities</li>
              <li><strong>Legal compliance:</strong> Processing required to comply with applicable laws</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Information Sharing and Disclosure</h2>
            <p className="text-slate-600 mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Service providers:</strong> Third-party vendors who assist in operating our platform (hosting, email services, AI processing)</li>
              <li><strong>Project stakeholders:</strong> When you choose to share documents or correspondence with other project participants</li>
              <li><strong>Legal authorities:</strong> When required by law or to protect our rights</li>
              <li><strong>Business transfers:</strong> In connection with any merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-slate-600 mt-4">
              We do not sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate technical and organisational measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and employee training</li>
              <li>Incident response procedures</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Retention</h2>
            <p className="text-slate-600">
              We retain your personal information for as long as necessary to fulfil the purposes outlined in this 
              policy, unless a longer retention period is required by law. Project data may be retained for the 
              duration required by construction industry regulations and contractual obligations, typically a 
              minimum of 5 years after project completion.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Your Rights</h2>
            <p className="text-slate-600 mb-4">Under POPIA, you have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Access your personal information held by us</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information (subject to legal requirements)</li>
              <li>Object to processing of your information</li>
              <li>Request restriction of processing</li>
              <li>Data portability</li>
              <li>Withdraw consent where processing is based on consent</li>
              <li>Lodge a complaint with the Information Regulator</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Cookies</h2>
            <p className="text-slate-600">
              We use cookies and similar technologies to enhance your experience, analyse usage patterns, and 
              provide personalised features. You can control cookie settings through your browser preferences. 
              Essential cookies required for the platform to function cannot be disabled.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. International Data Transfers</h2>
            <p className="text-slate-600">
              Your information may be transferred to and processed in countries outside South Africa where our 
              service providers operate. We ensure appropriate safeguards are in place to protect your information 
              in accordance with POPIA requirements.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-slate-600">
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new policy on our website and updating the "Last updated" date. Your continued use 
              of our services after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-slate-100 p-6 rounded-lg">
              <p className="text-slate-700"><strong>Gantt Account Management (Pty) Ltd</strong></p>
              <p className="text-slate-600">Information Officer</p>
              <p className="text-slate-600">Email: info@gantt-acc.co.za</p>
              <p className="text-slate-600">Phone: 073 049 1360</p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Gantt Account Management. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
